import { useEffect, useState } from "react";
import { Phone, PhoneOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";

interface IncomingCallDialogProps {
  callerName: string;
  callerAvatar?: string;
  onAccept: () => void;
  onDecline: (reason?: 'declined' | 'missed') => void;
}

export const IncomingCallDialog = ({
  callerName,
  callerAvatar,
  onAccept,
  onDecline,
}: IncomingCallDialogProps) => {
  const [isRinging, setIsRinging] = useState(true);

  useEffect(() => {
    // Play ringtone
    const audio = new Audio("/sounds/notification.mp3");
    audio.loop = true;
    audio.play().catch(() => {});

    // Auto-decline after 30 seconds (mark as missed)
    const timeout = setTimeout(() => {
      onDecline('missed');
    }, 30000);

    return () => {
      audio.pause();
      clearTimeout(timeout);
    };
  }, [onDecline]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="text-center"
        >
          {/* Animated rings */}
          <div className="relative inline-block mb-8">
            <motion.div
              animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="absolute inset-0 rounded-full bg-primary"
            />
            <motion.div
              animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0, 0.3] }}
              transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
              className="absolute inset-0 rounded-full bg-primary"
            />
            <img
              src={callerAvatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${callerName}`}
              alt={callerName}
              className="relative w-32 h-32 rounded-full border-4 border-primary z-10"
            />
          </div>

          <h2 className="text-white text-2xl font-bold mb-2">{callerName}</h2>
          <p className="text-white/60 mb-8">Incoming video call...</p>

          <div className="flex items-center justify-center gap-8">
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="destructive"
                size="icon"
                className="h-16 w-16 rounded-full"
                onClick={() => onDecline('declined')}
              >
                <PhoneOff className="h-7 w-7" />
              </Button>
            </motion.div>

            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
              <Button
                size="icon"
                className="h-16 w-16 rounded-full bg-green-500 hover:bg-green-600"
                onClick={onAccept}
              >
                <Phone className="h-7 w-7" />
              </Button>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
